"use client";

import {
  ArrowDownRight,
  ArrowRight,
  CalendarDays,
  Check,
  Facebook,
  Instagram,
  MapPin,
  Navigation,
  Phone,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState, type CSSProperties } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { club, gallery, locations, schedule } from "@/data/club";
import { seoFaqs } from "@/data/seo";

function Reveal({
  children,
  className,
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = elementRef.current;

    if (!element) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      element.classList.add("reveal-visible");
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        element.classList.add("reveal-visible");
        observer.disconnect();
      },
      { threshold: 0.16 },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      className={`reveal${className ? ` ${className}` : ""}`}
      ref={elementRef}
      style={{ "--reveal-delay": `${delay}s` } as CSSProperties}
    >
      {children}
    </div>
  );
}

function SectionHeading({
  eyebrow,
  title,
}: {
  eyebrow?: string;
  title: React.ReactNode;
}) {
  return (
    <div className="mb-10 flex w-full flex-col items-center text-center lg:mb-14">
      {eyebrow ? <p className="section-label">{eyebrow}</p> : null}
      <h2 className="section-title mt-3 w-full">{title}</h2>
    </div>
  );
}

const facts = [
  "Depuis 2009",
  "JJB + MMA + Grappling",
  "Kids, ados & adultes",
  "Cours d’essai offert",
  "Une seule équipe",
];

const weekDays = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

const disciplines = [
  {
    code: "MMA",
    title: "MMA + Grappling",
    image: "/images/gallery/collective-combat-training.jpg",
    alt: "Combattant d’Imana Academy entouré de son équipe après une victoire de MMA",
    objectPosition: "center top",
    description:
      "Percussion, lutte, contrôle et soumissions. L’adhésion unique donne accès au MMA, au JJB et au grappling.",
    tags: ["MMA + Grappling", "Adultes", "Tous niveaux"],
  },
  {
    code: "JJB",
    title: "Jiu-Jitsu Brésilien",
    image: "/images/imana-club-training.jpg",
    alt: "Quatre membres d’Imana Academy réunis après un entraînement de Jiu-Jitsu Brésilien",
    objectPosition: "42% center",
    description:
      "Placement, leviers et stratégie au sol. Apprends à prendre l’avantage avec précision, calme et intelligence.",
    tags: ["Dès 6 ans et demi", "15 ans +", "Loisir & compétition"],
  },
];

export default function HomePage() {
  const [discipline, setDiscipline] = useState<"TOUS" | "JJB" | "GRAPPLING" | "MMA">("TOUS");
  const heroImageRef = useRef<HTMLImageElement>(null);
  const heroVideoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = heroVideoRef.current;

    if (!video) return;

    const revealVideo = () => {
      if (video.paused) return;

      heroImageRef.current?.classList.replace("opacity-80", "opacity-0");
      video.classList.replace("opacity-0", "opacity-80");
    };
    const playVideo = () => {
      video.setAttribute("muted", "");
      video.setAttribute("playsinline", "");
      video.setAttribute("webkit-playsinline", "");
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      void video.play().then(revealVideo).catch(() => undefined);
    };
    const resumeVideo = () => {
      if (!document.hidden) playVideo();
    };

    const retryTimers = [0, 250, 900, 2200, 4500, 7000].map((delay) =>
      window.setTimeout(playVideo, delay),
    );

    video.addEventListener("loadedmetadata", playVideo);
    video.addEventListener("loadeddata", playVideo);
    video.addEventListener("canplay", playVideo);
    video.addEventListener("playing", revealVideo);
    video.addEventListener("pause", resumeVideo);
    window.addEventListener("pageshow", playVideo);
    document.addEventListener("visibilitychange", resumeVideo);
    document.addEventListener("pointerdown", playVideo, { once: true });
    document.addEventListener("touchstart", playVideo, { once: true, passive: true });

    return () => {
      retryTimers.forEach(window.clearTimeout);
      video.removeEventListener("loadedmetadata", playVideo);
      video.removeEventListener("loadeddata", playVideo);
      video.removeEventListener("canplay", playVideo);
      video.removeEventListener("playing", revealVideo);
      video.removeEventListener("pause", resumeVideo);
      window.removeEventListener("pageshow", playVideo);
      document.removeEventListener("visibilitychange", resumeVideo);
      document.removeEventListener("pointerdown", playVideo);
      document.removeEventListener("touchstart", playVideo);
    };
  }, []);
  const visibleSchedule =
    discipline === "TOUS"
      ? schedule
      : schedule.filter((group) => group.discipline === discipline);
  const visibleDays = weekDays.filter((day) =>
    visibleSchedule.some((group) =>
      group.sessions.some(([sessionDay]) => sessionDay === day),
    ),
  );
  const mobileSchedule = weekDays
    .map((day) => ({
      day,
      sessions: visibleSchedule.flatMap((group) =>
        group.sessions
          .filter(([sessionDay]) => sessionDay === day)
          .map(([, time, place]) => ({
            discipline: group.discipline,
            audience: group.audience,
            time,
            place,
          })),
      ),
    }))
    .filter(({ sessions }) => sessions.length > 0);

  return (
    <div className="overflow-hidden">
      <section
        id="accueil"
        className="relative isolate flex min-h-svh overflow-hidden bg-black text-white"
      >
          <Image
            preload
            alt="L’équipe Imana Academy réunie sur le tatami"
            className="object-cover object-center opacity-80 transition-opacity duration-1000"
            fill
            quality={55}
            ref={heroImageRef}
            sizes="100vw"
            src="/imana-hero-poster.jpg"
          />
          <video
            aria-hidden="true"
            autoPlay
            className="hero-video pointer-events-none absolute inset-0 size-full object-cover object-center opacity-0 transition-opacity duration-1000"
            disablePictureInPicture
            loop
            muted
            playsInline
            preload="metadata"
            ref={heroVideoRef}
          >
            <source src="/imana-hero-training.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,.12)_0%,rgba(0,0,0,.44)_48%,rgba(0,0,0,.92)_100%)] lg:bg-[linear-gradient(90deg,rgba(0,0,0,.94)_0%,rgba(0,0,0,.72)_43%,rgba(0,0,0,.12)_76%,rgba(0,0,0,.46)_100%)]" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-black/20 to-transparent lg:hidden" />
          <div className="animated-grid absolute inset-0 opacity-30 [background-image:linear-gradient(rgba(255,255,255,.055)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.055)_1px,transparent_1px)] [background-size:56px_56px] [mask-image:linear-gradient(to_bottom,black,transparent_88%)]" />

          <div className="hero-content relative z-10 mx-auto flex w-full max-w-[1480px] px-5 sm:px-8 lg:px-12 xl:px-16">
            <div className="w-full max-w-[920px] text-center lg:text-left">
              <h1 className="hero-title">
                On n’attend pas
                <br />
                d’être prêt.
                <br />
                <span className="text-primary">On le devient.</span>
              </h1>
              <p className="mt-5 text-[.62rem] font-extrabold uppercase tracking-[.16em] text-white/75">
                Club de MMA &amp; Jiu-Jitsu Brésilien · Savigny-le-Temple 77176 · Depuis 2009
              </p>
              <p className="mt-6 max-w-2xl text-[.95rem] font-medium leading-7 text-white/76 sm:text-base sm:leading-8">
                Ici, tu apprends à combattre, à garder la tête froide et à avancer avec
                une équipe qui ne laisse personne au bord du tatami.
              </p>
              <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row lg:items-stretch lg:justify-start">
                <Button asChild className="h-13 w-full sm:w-[252px]" size="xl">
                  <Link href="/inscription">
                    Essayer gratuitement
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
                <Button
                  asChild
                  className="h-13 w-full border-white/15 bg-white/8 text-white hover:border-white/30 hover:bg-white/14 hover:text-white sm:w-[252px]"
                  size="xl"
                  variant="outline"
                >
                  <Link href="#planning">
                    <CalendarDays />
                    Consulter les horaires
                  </Link>
                </Button>
              </div>
            </div>

          </div>

          <a
            aria-label="Découvrir Imana Academy"
            className="absolute bottom-5 right-5 z-20 hidden size-12 place-items-center rounded-[.3rem] border border-white/15 bg-black/35 text-white backdrop-blur-md transition hover:bg-white hover:text-black lg:grid"
            href="#club"
          >
            <ArrowDownRight className="size-5" />
          </a>
      </section>

      <section aria-label="Imana Academy en bref" className="facts-strip border-y border-border bg-card">
        <p className="sr-only">
          Depuis 2009, Imana Academy propose du MMA et du JJB pour les enfants, les adolescents et les adultes dans deux gymnases à Savigny-le-Temple 77176.
        </p>
        <div aria-hidden="true" className="facts-track">
          {[...facts, ...facts].map((fact, index) => (
            <span key={`${fact}-${index}`} className="facts-item">
              {fact}
              <i />
            </span>
          ))}
        </div>
      </section>

      <section id="club" className="section-shell">
        <Reveal>
          <SectionHeading
            title={
              <>
                Pas une salle où l’on se croise.
                <br />
                <span className="text-primary">Une équipe où l’on grandit.</span>
              </>
            }
          />
        </Reveal>

        <div>
          <Reveal>
            <div className="group relative min-h-[430px] overflow-hidden rounded-[.45rem] border border-border bg-card lg:aspect-[2040/941] lg:min-h-0">
              <Image
                alt="Quatre membres d’Imana Academy réunis après un entraînement de Jiu-Jitsu Brésilien"
                className="object-cover object-[42%_center] transition-transform duration-700 group-hover:scale-[1.025] lg:object-center"
                fill
                quality={62}
                sizes="(max-width: 1024px) 100vw, 92vw"
                src="/images/imana-club-training.jpg"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/18 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8">
                <Badge className="mb-4 bg-primary text-white">IMANA DEPUIS 2009</Badge>
                <p className="max-w-xl font-display text-3xl font-black uppercase leading-[.95] text-white sm:text-5xl">
                  Le niveau monte quand le collectif tient.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
        <Reveal className="mt-5 flex flex-col items-center justify-between gap-5 text-center sm:flex-row sm:text-left">
          <div>
            <p className="font-display text-2xl font-black uppercase">Derrière chaque séance, une équipe.</p>
            <p className="mt-1 text-sm text-muted-foreground">Découvre les trois coachs qui font vivre Imana Academy.</p>
          </div>
          <Button asChild variant="outline">
            <Link href="/coachs">Rencontrer les coachs <ArrowRight /></Link>
          </Button>
        </Reveal>
      </section>

      <section id="disciplines" className="section-shell section-tint">
        <Reveal>
          <SectionHeading
            title={
              <>
                Deux terrains.
                <br />
                <span className="text-primary">Une même exigence.</span>
              </>
            }
          />
        </Reveal>
        <div className="grid gap-4 lg:grid-cols-2">
          {disciplines.map((item, index) => (
            <Reveal key={item.code} delay={index * 0.08}>
              <article className="discipline-card group relative min-h-[520px] overflow-hidden rounded-[.45rem] border border-border bg-card transition-transform duration-300 hover:-translate-y-1.5 motion-reduce:transform-none">
                <Image
                  alt={item.alt}
                  className="object-cover transition-transform duration-700 group-hover:scale-[1.025]"
                  fill
                  quality={62}
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  src={item.image}
                  style={{ objectPosition: item.objectPosition }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/45 to-black/5" />
                <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8">
                  <h3 className="font-display text-4xl font-black uppercase leading-[.95] text-white sm:text-5xl">
                    {item.title} <span className="text-primary">({item.code})</span>
                  </h3>
                  <p className="mt-4 max-w-xl text-sm font-medium leading-7 text-white/78">
                    {item.description}
                  </p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {item.tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-[.22rem] border border-white/14 bg-black/30 px-3 py-1.5 text-[.6rem] font-extrabold uppercase tracking-[.1em] text-white/75 backdrop-blur"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="planning" className="section-shell">
        <Reveal>
          <SectionHeading
            title={
              <>
                Ton prochain entraînement
                <br />
                <span className="text-primary">commence ici.</span>
              </>
            }
          />
        </Reveal>
        <Reveal>
          <div className="mb-5 flex justify-center">
            <div
              aria-label="Filtrer le planning par discipline"
              className="grid h-12 w-full max-w-[560px] grid-cols-4 rounded-[.35rem] border border-border bg-card p-1 shadow-[inset_0_1px_0_rgba(255,255,255,.05)]"
              role="group"
            >
              {(["TOUS", "JJB", "GRAPPLING", "MMA"] as const).map((value) => {
                const isActive = discipline === value;

                return (
                  <button
                    aria-pressed={isActive}
                    className={`inline-flex min-w-0 items-center justify-center rounded-[.18rem] border border-transparent px-2 py-2 text-[.7rem] font-extrabold tracking-[.04em] transition-[background-color,color,box-shadow] duration-300 focus-visible:border-ring focus-visible:outline-1 focus-visible:outline-ring focus-visible:ring-[3px] focus-visible:ring-ring/30 sm:px-4 sm:text-xs ${
                      isActive
                        ? "bg-primary text-white shadow-[0_4px_14px_rgba(181,42,61,.2)]"
                        : "text-muted-foreground hover:bg-background hover:text-foreground"
                    }`}
                    key={value}
                    onClick={() => setDiscipline(value)}
                    type="button"
                  >
                    {value === "TOUS" ? "Tous" : value === "GRAPPLING" ? "Grappling" : value}
                  </button>
                );
              })}
            </div>
          </div>
        </Reveal>

        <div
          key={`desktop-${discipline}`}
          className="schedule-board schedule-panel hidden overflow-x-auto border border-border bg-card md:block"
          tabIndex={0}
        >
          <table className="w-full min-w-[880px] border-collapse text-left">
            <caption className="sr-only">Planning hebdomadaire des cours Imana Academy</caption>
            <thead>
              <tr className="border-b border-border">
                <th className="w-28 bg-muted/45 px-5 py-5 text-[.62rem] font-extrabold uppercase tracking-[.14em] text-muted-foreground">
                  Jour
                </th>
                {visibleSchedule.map((group) => (
                  <th
                    key={group.discipline + group.audience}
                    className="min-w-44 border-l border-border px-5 py-5"
                  >
                    <span className="block text-[.6rem] font-extrabold uppercase tracking-[.16em] text-primary-readable">
                      {group.discipline}
                    </span>
                    {group.audience ? (
                      <span className="mt-1.5 block text-xs font-extrabold uppercase text-foreground">
                        {group.audience}
                      </span>
                    ) : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleDays.map((day) => (
                <tr key={day} className="border-b border-border last:border-b-0 hover:bg-accent/45">
                  <th className="bg-muted/25 px-5 py-5 font-display text-lg font-black uppercase text-foreground">
                    {day.slice(0, 3)}.
                  </th>
                  {visibleSchedule.map((group) => {
                    const session = group.sessions.find(([sessionDay]) => sessionDay === day);

                    return (
                      <td
                        key={group.discipline + group.audience}
                        className="border-l border-border px-5 py-4"
                      >
                        {session ? (
                          <>
                            <time className="block font-display text-xl font-black text-foreground">
                              {session[1]}
                            </time>
                            <span className="mt-1 block text-[.62rem] font-semibold uppercase tracking-[.08em] text-muted-foreground">
                              {session[2]}
                            </span>
                          </>
                        ) : (
                          <span className="text-muted-foreground/70">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div
          key={`mobile-${discipline}`}
          className="schedule-panel grid gap-3 md:hidden"
        >
          {mobileSchedule.map(({ day, sessions: daySessions }) => (
            <section key={day} className="overflow-hidden rounded-[.25rem] border border-border bg-card">
              <header className="border-b border-border bg-muted/45 px-4 py-3.5">
                <h3 className="w-full text-center font-display text-2xl font-black uppercase text-foreground">
                  {day}
                </h3>
              </header>
              <div className="divide-y divide-border px-4">
                {daySessions.map((session) => (
                  <article
                    key={`${session.discipline}-${session.audience}-${session.time}`}
                    className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 py-4"
                  >
                    <div className="min-w-0">
                      <p className="text-[.58rem] font-extrabold uppercase tracking-[.16em] text-primary-readable">
                        {session.discipline}
                      </p>
                      {session.audience ? (
                        <h4 className="mt-1 font-display text-lg font-black uppercase leading-none text-foreground">
                          {session.audience}
                        </h4>
                      ) : null}
                      <p className="mt-2 flex items-center gap-1.5 text-[.6rem] font-semibold uppercase tracking-[.08em] text-muted-foreground">
                        <MapPin className="size-3 text-primary" />
                        {session.place}
                      </p>
                    </div>
                    <time className="whitespace-nowrap text-right font-display text-xl font-black leading-none text-foreground">
                      {session.time}
                    </time>
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>
      </section>

      <section id="salles" className="section-shell section-tint">
        <Reveal>
          <SectionHeading
            title={
              <>
                Le bon lieu
                <br />
                <span className="text-primary">pour ton rythme.</span>
              </>
            }
          />
          <p className="mx-auto -mt-5 mb-10 max-w-2xl text-center text-sm font-medium leading-7 text-muted-foreground sm:-mt-8 sm:mb-14 sm:text-base sm:leading-8">
            Jean Zay ou Léo Lagrange : choisis le créneau qui s’intègre à ta semaine.
            Dans les deux salles, tu retrouves la même équipe, la même exigence et un
            accueil pensé pour progresser dès le premier cours.
          </p>
        </Reveal>
        <div className="grid min-w-0 gap-4 lg:grid-cols-2">
          {locations.map((location, index) => (
            <Reveal key={location.name} className="min-w-0" delay={index * 0.06}>
              <article className="venue-card w-full min-w-0 max-w-full overflow-hidden rounded-[.4rem] border border-border bg-card">
                <div className="venue-visual group relative h-[190px] w-full min-w-0 overflow-hidden border-b border-border bg-black sm:h-[230px] lg:aspect-[2.4/1] lg:h-auto">
                  <Image
                    alt={`Interprétation cartographique de ${location.name}`}
                    className="object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                    fill
                    quality={62}
                    sizes="(max-width: 1024px) 100vw, 50vw"
                    src={location.image}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/5 to-black/20" />
                  <p className="absolute left-5 top-5 border-l-2 border-primary pl-3 text-[.6rem] font-extrabold uppercase tracking-[.16em] text-white/78">
                    Savigny-le-Temple 77176
                  </p>
                  <p className="absolute inset-x-5 bottom-5 font-display text-4xl font-black uppercase leading-none text-white sm:text-5xl">
                    {location.name.replace("Gymnase ", "")}
                  </p>
                </div>
                <div className="flex min-w-0 flex-col gap-5 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
                  <p className="min-w-0 max-w-xs break-words text-sm font-medium leading-6 text-muted-foreground">
                    {location.address}
                  </p>
                  <Button asChild className="w-full min-w-0 shrink sm:w-auto sm:shrink-0" size="lg">
                    <a
                      aria-label={`Ouvrir ${location.name} dans le GPS`}
                      href={location.maps}
                      rel="noreferrer"
                      target="_blank"
                    >
                      <Navigation />
                      Ouvrir dans le GPS
                    </a>
                  </Button>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="galerie" className="section-shell">
        <Reveal>
          <SectionHeading
            title={
              <>
                L’énergie se vit mieux
                <br />
                <span className="text-primary">qu’elle ne se raconte.</span>
              </>
            }
          />
        </Reveal>
        <div className="columns-2 gap-3 lg:columns-3">
          {gallery.map((item, index) => (
            <Reveal key={item.src} className="mb-3 break-inside-avoid" delay={index * 0.04}>
              <figure className="group relative overflow-hidden rounded-[.3rem] border border-border bg-black">
                <Image
                  alt={item.alt}
                  className="h-auto w-full transition-transform duration-700 group-hover:scale-[1.015]"
                  height={item.height}
                  quality={62}
                  sizes="(max-width: 1024px) 50vw, 33vw"
                  src={item.src}
                  width={item.width}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/72 via-transparent to-transparent opacity-75 transition-opacity group-hover:opacity-100" />
                <figcaption className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-3 p-4 text-[.58rem] font-extrabold uppercase tracking-[.13em] text-white/78">
                  <span>Imana Academy</span>
                  <span className="h-px w-8 bg-primary" />
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="faq" className="section-shell section-tint">
        <Reveal>
          <SectionHeading
            eyebrow="Questions fréquentes"
            title={
              <>
                Tout ce qu’il faut savoir
                <br />
                <span className="text-primary">avant de monter sur le tapis.</span>
              </>
            }
          />
        </Reveal>
        <Reveal className="mx-auto max-w-4xl overflow-hidden rounded-[.4rem] border border-border bg-card px-5 sm:px-8">
          <Accordion collapsible type="single">
            {seoFaqs.map((item, index) => (
              <AccordionItem key={item.question} value={`faq-${index}`}>
                <AccordionTrigger className="py-5 font-display text-lg font-black uppercase leading-tight sm:text-2xl">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="max-w-3xl pb-6 text-sm font-medium leading-7 text-muted-foreground sm:text-base sm:leading-8">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </section>

      <section id="contact" className="px-3 pb-3 pt-3 sm:px-5 sm:pb-5 sm:pt-5 lg:px-8 lg:pb-8 lg:pt-8">
        <Reveal>
          <div className="relative mx-auto min-h-[500px] max-w-[1416px] overflow-hidden rounded-[.5rem] border border-white/10 bg-[#070707] text-white">
            <div className="animated-grid absolute inset-0 opacity-40 [background-image:linear-gradient(rgba(255,255,255,.035)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.035)_1px,transparent_1px)] [background-size:56px_56px]" />
            <div className="relative z-10 grid min-h-[500px] items-center gap-10 p-6 sm:p-10 lg:grid-cols-12 lg:p-16">
              <div className="text-center lg:col-span-8 lg:text-left">
                <h2 className="mt-5 font-display text-[clamp(3.5rem,8vw,7.8rem)] font-black uppercase leading-[.88] tracking-[-.012em]">
                  Viens pour essayer.
                  <br />
                  <span className="text-primary">Reviens pour progresser.</span>
                </h2>
                <p className="mt-6 max-w-2xl text-sm font-medium leading-7 text-white/78 sm:text-base sm:leading-8">
                  Aucun prérequis. On te présente le coach, le groupe et le cours adapté
                  à ton âge comme à ton objectif. Tu n’as plus qu’à monter sur le tapis.
                </p>
                <div className="mt-6 flex flex-wrap justify-center gap-x-5 gap-y-3 text-[.65rem] font-extrabold uppercase tracking-[.1em] text-white/80 lg:justify-start">
                  {["Sans engagement", "Tous niveaux", "Accueil guidé"].map((item) => (
                    <span key={item} className="flex items-center gap-2">
                      <Check className="size-4 text-primary" />
                      {item}
                    </span>
                  ))}
                </div>
              </div>
              <div className="border-t border-white/12 pt-7 text-center lg:col-span-4 lg:border-l lg:border-t-0 lg:pl-10 lg:pt-0 lg:text-left">
                <p className="font-display text-3xl font-black uppercase">Prêt à commencer ?</p>
                <p className="mt-3 text-sm leading-6 text-white/75">
                  Remplis le formulaire et viens découvrir le club. Ton premier cours est offert.
                </p>
                <Button asChild className="mt-6 h-13 w-full" size="xl">
                  <Link href="/inscription">
                    Profiter du cours offert
                    <ArrowRight />
                  </Link>
                </Button>
                <Button
                  asChild
                  className="mt-3 h-13 w-full border-white/12 bg-white/5 text-white hover:bg-white/10 hover:text-white"
                  size="xl"
                  variant="outline"
                >
                  <a href={locations[0].maps} rel="noreferrer" target="_blank">
                    <MapPin />
                    Voir la salle
                  </a>
                </Button>
                <div className="mt-7 grid w-full grid-cols-2 gap-3 border-t border-white/12 pt-6 text-sm font-semibold text-white/70">
                  {club.phones.map((phone) => (
                    <a
                      key={phone.href}
                      aria-label={`Appeler Imana Academy au ${phone.label}`}
                      className="group flex min-h-11 w-full min-w-0 items-center justify-center gap-2 border border-primary/45 bg-primary/10 px-2 text-[.62rem] font-extrabold tracking-[.06em] text-white transition-all hover:-translate-y-0.5 hover:border-primary hover:bg-primary hover:shadow-[0_8px_20px_rgba(181,42,61,.22)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-black sm:gap-3 sm:px-4 sm:text-[.7rem] sm:tracking-[.08em]"
                      href={phone.href}
                    >
                      <Phone className="size-4 shrink-0 text-primary transition-colors group-hover:text-white" />
                      {phone.label}
                    </a>
                  ))}
                  <div className="col-span-2 mt-1 grid w-full grid-cols-2 gap-3">
                    <a
                      aria-label="Ouvrir la page Facebook d'Imana Academy"
                      className="group/social flex min-h-11 min-w-0 w-full items-center justify-center gap-2.5 border border-[#1877f2]/70 bg-[#1877f2] px-2 text-[.68rem] font-extrabold uppercase tracking-[.08em] text-white shadow-[0_8px_20px_rgba(24,119,242,.18)] transition-all hover:-translate-y-0.5 hover:border-[#4b96f5] hover:bg-[#0d6bdd] hover:shadow-[0_12px_26px_rgba(24,119,242,.3)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1877f2] focus-visible:ring-offset-2 focus-visible:ring-offset-black sm:px-4"
                      href={club.facebook}
                      rel="noreferrer"
                      target="_blank"
                    >
                      <Facebook className="size-4 fill-white text-white transition-transform group-hover/social:scale-110" />
                      Facebook
                    </a>
                    <a
                      aria-label="Ouvrir la page Instagram d'Imana Academy"
                      className="group/social flex min-h-11 min-w-0 w-full items-center justify-center gap-2.5 border border-[#e4405f]/70 bg-[linear-gradient(135deg,#833ab4_0%,#e1306c_48%,#f77737_100%)] px-2 text-[.68rem] font-extrabold uppercase tracking-[.08em] text-white shadow-[0_8px_20px_rgba(225,48,108,.2)] transition-all hover:-translate-y-0.5 hover:border-[#ffad73] hover:shadow-[0_12px_26px_rgba(225,48,108,.32)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e1306c] focus-visible:ring-offset-2 focus-visible:ring-offset-black sm:px-4"
                      href={club.instagram}
                      rel="noreferrer"
                      target="_blank"
                    >
                      <Instagram className="size-4 text-white transition-transform group-hover/social:scale-110" />
                      Instagram
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <footer className="border-t border-border">
        <div className="mx-auto grid max-w-[1480px] gap-6 px-5 py-8 text-center text-[.62rem] font-bold uppercase tracking-[.1em] text-muted-foreground sm:px-8 lg:grid-cols-[1fr_auto] lg:items-end lg:px-8 lg:text-left">
          <div className="space-y-2 leading-5">
            <p className="text-foreground">© {new Date().getFullYear()} Imana Academy · Savigny-le-Temple 77176</p>
            <p>IMANA CONTACT · Association déclarée loi 1901 · SIREN 803 502 293 · RNA W772001351</p>
            <p>Siège social · 25 boîte 10, rue de Rougeau · 77176 Savigny-le-Temple</p>
          </div>
          <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 lg:justify-end">
            <Link className="transition-colors hover:text-foreground" href="/mentions-legales">
              Mentions légales
            </Link>
            <a className="transition-colors hover:text-foreground" href={club.instagram} rel="noreferrer" target="_blank">
              Instagram
            </a>
            <a className="transition-colors hover:text-foreground" href={club.facebook} rel="noreferrer" target="_blank">
              Facebook
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
